import React, { useEffect, useMemo, useState } from "react";

import {
  adminUpdateOrg,
  AdminOrgDetail,
  AdminOrgRow,
  getOrgDetail,
  impersonate,
  searchOrgs,
} from "./API";
import { TextInput } from "@dooglie/ui";
import TimezoneSelect from "./components/TimezoneSelect";
import { actionErrorMessage } from "./settings/actionErrorMessage";
import "./styles/adminpanel.css";

// Profile fields an admin may edit (mirrors backend ALLOWED_ORG_FIELDS).
const EDITABLE_FIELDS: {
  key: string;
  label: string;
  type: "text" | "number" | "checkbox" | "timezone";
}[] = [
  { key: "name", label: "Name", type: "text" },
  { key: "company_domain", label: "Company domain", type: "text" },
  { key: "account_notes", label: "Account notes", type: "text" },
  { key: "contracted_seats", label: "Contracted seats (floor)", type: "number" },
  { key: "price_per_user", label: "Price per user", type: "number" },
  { key: "is_monthly", label: "Monthly billing", type: "checkbox" },
  { key: "currency", label: "Currency (usd/gbp/eur)", type: "text" },
  { key: "timezone", label: "Timezone", type: "timezone" },
  { key: "start_date", label: "Start date (YYYY-MM-DD)", type: "text" },
  { key: "end_date", label: "End date (YYYY-MM-DD)", type: "text" },
];

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "—");

const accessBadge = (org: AdminOrgRow) => {
  if (org.subscription_status) return org.subscription_status.replace(/_/g, " ");
  if (org.trial_end_at && new Date(org.trial_end_at) > new Date()) return "trial";
  return "no access";
};

const AdminPanel: React.FC = () => {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<AdminOrgRow[]>([]);
  const [selected, setSelected] = useState<AdminOrgDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setRows(await searchOrgs(query));
      setError(null);
    } catch {
      setError("Not authorised, or failed to load orgs.");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openOrg = async (orgId: string) => {
    setCreating(false);
    setError(null);
    try {
      setSelected(await getOrgDetail(orgId));
    } catch {
      setError("Failed to load org detail.");
    }
  };

  return (
      <div className="dg-card dg-ap-layout">
      <div className="dg-ap-sidebar">
        <h2 className="dg-ap-title">Organisations</h2>
        <div className="dg-ap-toolbar">
          <TextInput
            className="dg-ap-search"
            field="admin.org_search"
            hint="An organisation's name"
            value={query}
            placeholder="Search by name…"
            inputProps={{ "aria-label": "Search organisations" }}
            onValueChange={setQuery}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
          <button type="button" className="dg-btn dg-btn-primary dg-btn-sm" onClick={load}>
            Search
          </button>
          <button
            type="button"
            className="dg-btn dg-btn-ghost dg-btn-sm"
            onClick={() => {
              setSelected(null);
              setCreating(true);
            }}
          >
            + New
          </button>
        </div>
        {error && <div className="dg-ap-error">{error}</div>}
        <table className="dg-ap-org-table">
          <tbody>
            {rows.map((org) => (
              <tr
                key={org.org_id}
                className="dg-ap-org-row"
                onClick={() => openOrg(org.org_id)}
              >
                <td style={{ padding: "10px 4px" }}>
                  <div className="dg-ap-org-name">{org.name}</div>
                  <div className="dg-ap-org-meta">{org.company_domain}</div>
                </td>
                <td style={{ textAlign: "right", padding: "10px 4px" }}>
                  <div>
                    {org.seats_used}/{org.paid_seats || org.contracted_seats} seats
                    {org.over_seat ? " (over seat)" : ""}
                  </div>
                  <div className="dg-ap-org-meta">
                    {org.members_total} members
                  </div>
                  <div
                    className={`dg-ap-org-meta ${
                      org.has_access ? "dg-ap-org-status--ok" : "dg-ap-org-status--bad"
                    }`}
                  >
                    {accessBadge(org)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="dg-ap-main">
        {creating ? (
          <OrgEditor
            key="new"
            onSaved={async (orgId) => {
              setCreating(false);
              await load();
              await openOrg(orgId);
            }}
          />
        ) : selected ? (
          <OrgDetailView key={selected.org_id} org={selected} onChanged={() => openOrg(selected.org_id)} />
        ) : (
          <div className="dg-ap-empty">Select an organisation, or create a new one.</div>
        )}
      </div>
      </div>
  );
};

const OrgEditor: React.FC<{
  org?: AdminOrgDetail;
  onSaved: (orgId: string) => void;
}> = ({ org, onSaved }) => {
  const [fields, setFields] = useState<Record<string, unknown>>(() => {
    const init: Record<string, unknown> = {};
    EDITABLE_FIELDS.forEach((f) => {
      init[f.key] = org
        ? (org as unknown as Record<string, unknown>)[f.key] ?? ""
        : f.type === "checkbox"
          ? false
          : "";
    });
    init.admin_users = org ? (org.admin_users || []).join(", ") : "";
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payloadFields: Record<string, unknown> = { ...fields };
      payloadFields.admin_users = String(fields.admin_users || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await adminUpdateOrg({ org_id: org?.org_id, fields: payloadFields });
      onSaved(res.org_id);
    } catch (e) {
      // A refused value (a 400 such as a timezone the backend cannot load) comes
      // back as the response body with its reason in `error`.
      setErr(actionErrorMessage(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="dg-ap-title">{org ? "Edit organisation" : "Create organisation"}</h2>
      {err && <div className="dg-ap-error">{err}</div>}
      <table className="dg-ap-form-table">
        <tbody>
          {EDITABLE_FIELDS.map((f) => (
            <tr key={f.key}>
              <td>{f.label}</td>
              <td>
                {f.type === "checkbox" ? (
                  <input
                    type="checkbox"
                    checked={Boolean(fields[f.key])}
                    onChange={(e) => setFields({ ...fields, [f.key]: e.target.checked })}
                  />
                ) : f.type === "timezone" ? (
                  <TimezoneSelect
                    aria-label={f.label}
                    value={String(fields[f.key] ?? "")}
                    onChange={(tz) => setFields({ ...fields, [f.key]: tz })}
                  />
                ) : f.type === "number" ? (
                  <input
                    type="number"
                    aria-label={f.label}
                    value={String(fields[f.key] ?? "")}
                    onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                  />
                ) : (
                  <TextInput
                    className="dg-ap-field"
                    fullWidth
                    field={`admin.${f.key}`}
                    hint={f.label}
                    inputProps={{ "aria-label": f.label }}
                    value={String(fields[f.key] ?? "")}
                    onValueChange={(text) => setFields({ ...fields, [f.key]: text })}
                  />
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td>Admin emails (comma-sep)</td>
            <td>
              <TextInput
                className="dg-ap-field"
                fullWidth
                field="admin.admin_users"
                hint="Email addresses, comma separated"
                inputProps={{ "aria-label": "Admin emails" }}
                value={String(fields.admin_users ?? "")}
                onValueChange={(text) => setFields({ ...fields, admin_users: text })}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <button
        type="button"
        className="dg-btn dg-btn-primary"
        onClick={save}
        disabled={saving}
        style={{ marginTop: 12 }}
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
};

const OrgDetailView: React.FC<{ org: AdminOrgDetail; onChanged: () => void }> = ({ org, onChanged }) => {
  const [msg, setMsg] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const runAction = async (body: Parameters<typeof adminUpdateOrg>[0], label: string) => {
    setMsg(null);
    try {
      const res = await adminUpdateOrg(body);
      if (res.checkout_url) {
        // Checkout links are meant to be sent to the customer — show to copy.
        setMsg(`Checkout link: ${res.checkout_url}`);
      } else {
        setMsg(`${label} done.`);
      }
      onChanged();
    } catch (e) {
      setMsg(actionErrorMessage(e, `${label} failed.`));
    }
  };

  // Act as a member: every request after this runs as them (staff guards
  // stop real writes on a customer org), so the page reloads into their view.
  const actAs = async (userId: string) => {
    setMsg(null);
    try {
      await impersonate({ user_id: userId });
      window.location.reload();
    } catch (e) {
      setMsg(actionErrorMessage(e, "Act as failed."));
    }
  };

  const billed = useMemo(() => Boolean(org.stripe_subscription_id), [org]);

  if (editing) {
    return (
      <OrgEditor
        org={org}
        onSaved={() => {
          setEditing(false);
          onChanged();
        }}
      />
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>{org.name}</h2>
        <button type="button" className="dg-btn dg-btn-ghost dg-btn-sm" onClick={() => setEditing(true)}>
          Edit fields
        </button>
      </div>
      <div style={{ color: "#8890ab" }}>{org.company_domain}</div>
      {msg && (
        <div style={{ marginTop: 12, padding: 10, background: "#f4f6ff", borderRadius: 6, wordBreak: "break-all" }}>
          {msg}
        </div>
      )}

      <section style={{ marginTop: 20 }}>
        <h3>Billing</h3>
        <div>
          Status: <b>{accessBadge(org)}</b> · access: {org.has_access ? "yes" : "no"}
        </div>
        <div>
          Seats: {org.seats_used} given out / {org.paid_seats} paid (floor {org.contracted_seats})
          · {org.members_total} members{org.over_seat ? " · over seat" : ""}
        </div>
        <div>
          Plan: {org.currency} {org.price_per_user} / {org.is_monthly ? "month" : "year"}
        </div>
        <div>
          Trial ends: {fmtDate(org.trial_end_at)} · Period end: {fmtDate(org.current_period_end)}
        </div>
        <div>
          Stripe: {org.stripe_customer_id || "—"} / {org.stripe_subscription_id || "—"}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {!billed && (
            <button
              onClick={() => runAction({ org_id: org.org_id, action: "issue_subscription" }, "Issue subscription")}
            >
              Issue subscription (get checkout link)
            </button>
          )}
          <button
            onClick={() => {
              const n = window.prompt("New seat quantity", String(org.paid_seats || org.contracted_seats));
              if (n) runAction({ org_id: org.org_id, action: "change_seats", seats: Number(n) }, "Change seats");
            }}
            disabled={!billed}
          >
            Change seats
          </button>
          <button
            onClick={() => {
              const d = window.prompt("Trial length in days", "14");
              if (d) runAction({ org_id: org.org_id, action: "set_trial", trial_days: Number(d) }, "Set trial");
            }}
          >
            Start / extend trial
          </button>
          {org.trial_end_at && new Date(org.trial_end_at) > new Date() && (
            <button
              onClick={() => {
                if (
                  window.confirm(
                    "End this org's trial now? It will lose access unless it has a live subscription.",
                  )
                )
                  runAction({ org_id: org.org_id, action: "clear_trial" }, "End trial");
              }}
            >
              End trial
            </button>
          )}
        </div>
      </section>

      <section style={{ marginTop: 20 }}>
        <h3>Members ({org.members.length})</h3>
        <table style={{ borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            {org.members.map((m) => (
              <tr key={m.email} style={{ borderBottom: "1px solid #f0f2f8" }}>
                <td style={{ padding: "6px 12px 6px 0" }}>{m.name || "—"}</td>
                <td style={{ padding: "6px 12px 6px 0" }}>{m.email}</td>
                <td style={{ padding: "6px 12px 6px 0" }}>{m.is_admin ? "admin" : "member"}</td>
                <td style={{ padding: "6px 12px 6px 0", color: m.has_seat ? "#2e8b57" : "#c0392b" }}>
                  {m.has_seat ? "seat" : "no seat"}
                </td>
                <td style={{ padding: "6px 0" }}>
                  {m.user_id && (
                    <button
                      type="button"
                      className="dg-btn dg-btn-ghost dg-btn-sm"
                      aria-label={`Act as ${m.email}`}
                      onClick={() => void actAs(m.user_id!)}
                    >
                      Act as
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <SyncSection org={org} />
    </div>
  );
};

// CRM data is ingested on demand (attention-driven) now — there is no background
// sync to display or control here; this section just shows provisioning state.
const SyncSection: React.FC<{ org: AdminOrgDetail }> = ({ org }) => {
  return (
    <section style={{ marginTop: 20 }}>
      <h3>Provisioning</h3>
      <div>CRM provider: {org.crm_provider || "—"}</div>
      <div>Knowledge base provisioned: {org.sync.vector_store_provisioned ? "yes" : "no"}</div>
      <div>Assistant uses: {org.assistant_uses}</div>
    </section>
  );
};

export default AdminPanel;
